import { BRIDGE_VERSION, event } from '@budovan/bridge';
import { type Bridge, createBridge } from './adapter/bridge.ts';
import { connectLive, fetchManifest } from './adapter/platform.ts';
import { type PlaceState, tintFor } from './adapter/state.ts';
import { createViewer } from './core/viewer.ts';
import { type Panel, createPanel } from './ui/panel.ts';
import './style.css';

/* Entry point: one URL scheme for the app WebView (?app=1, UI hidden, bridge on; ?warm=1 only fills
 * the cache), public pages and embeds. ?building=B0001 picks the building; a token comes from the app over the bridge, or for local
 * testing in the URL fragment (#access_token=…, never sent to a server). */

const params = new URLSearchParams(window.location.search);
const buildingCode = params.get('building') ?? 'B0001';
const inApp = params.get('app') === '1';
const config = await fetch('/config.json', { cache: 'no-cache' })
  .then((response) => (response.ok ? (response.json() as Promise<{ apiUrl?: string }>) : {}))
  .catch(() => ({}));
const apiUrl = (config as { apiUrl?: string }).apiUrl ?? 'http://localhost:3000';
let token = new URLSearchParams(window.location.hash.slice(1)).get('access_token');

// ?warm=1: the app fills the HTTP cache from Home once it is idle (a hidden WebView or iframe), so the
// first real open reads the bundle and the model from disk. The modules are fetched and parsed by the
// imports above; this fetches the model too, then stops before any engine, canvas or socket exists.
if (params.get('warm') === '1') {
  const manifest = await fetchManifest(apiUrl, buildingCode, token).catch(() => null);
  if (manifest?.model) await fetch(new URL(manifest.model.url, window.location.href)).catch(() => null);
  await new Promise<never>(() => undefined);
}

const canvas = document.querySelector('canvas') as HTMLCanvasElement;
const panelRoot = document.querySelector('#panel') as HTMLElement;
panelRoot.hidden = inApp;
const viewer = createViewer(canvas);

let states = new Map<string, PlaceState>();
// Free and booked for the range the app is booking in; wins over the live state while set.
let availability = new Map<string, 'free' | 'booked'>();
let selected: string | null = null;
let placeNames = new Map<string, string>();
let labelCodes: string[] = [];
// Tags show the place's name and whether it is free in the range being booked (else its live state).
const relabel = (): void => {
  viewer.setLabels(
    labelCodes.flatMap((code) => {
      const status = availability.size > 0 ? availability.get(code) : states.get(code)?.booking;
      const text = placeNames.get(code);
      return text && (status === 'free' || status === 'booked') ? [{ code, text, status }] : [];
    }),
  );
};
let closeLive: (() => void) | null = null;
let bridgeSend: Bridge['send'] = () => undefined;

const repaint = (codes: Iterable<string>): void => {
  // While the app books a range, that range decides: a place it left out (filtered) is neutral, never
  // coloured by its live state, which is about now and not the range.
  const booking = availability.size > 0;
  for (const code of codes) {
    const status = availability.get(code);
    const state = states.get(code);
    let shown = state;
    if (status) shown = { code, readings: {}, ...state, booking: status };
    else if (booking) shown = { code, readings: {}, booking: 'not_bookable' };
    viewer.tint(code, tintFor(shown, code === selected));
  }
};

// The model loads once: start() runs again when the app hands over a token, and only the data refreshes.
let modelLoad: Promise<void> | null = null;
let panel: Panel | null = null;

function select(code: string): void {
  const previous = selected;
  selected = code;
  repaint(previous ? [previous, code] : [code]);
  viewer.focus(code);
  panel?.showDetail(code);
  bridgeSend(event('placeTapped', { code }));
}
viewer.onPick(select);
(window as unknown as { budovanTwin: { select(code: string): void } }).budovanTwin = { select };

async function start(): Promise<void> {
  const manifest = await fetchManifest(apiUrl, buildingCode, token);
  document.title = `${manifest.building.name} · Budovan`;
  const mapped = manifest.places.filter((place) => place.nodeName);
  if (manifest.model && !modelLoad) {
    modelLoad = viewer
      .load(
        new URL(manifest.model.url, window.location.href).toString(),
        Object.fromEntries(mapped.map((place) => [place.nodeName as string, place.code])),
      )
      .catch((error: unknown) => {
        modelLoad = null;
        throw error;
      });
  }
  await modelLoad;
  placeNames = new Map(manifest.places.map((place) => [place.code, place.name]));
  repaint(mapped.map((place) => place.code));
  relabel();
  panel ??= inApp
    ? null
    : createPanel(panelRoot, manifest, {
        floor: (number) => viewer.showFloor(number),
        select: (code) => select(code),
      });
  closeLive?.();
  if (token && manifest.mode === 'signed_in') {
    closeLive = connectLive(apiUrl, buildingCode, token, (next, changed) => {
      states = next;
      repaint(changed);
      panel?.setStates(states);
    });
  }
  bridgeSend(event('ready', { bridgeVersion: BRIDGE_VERSION, buildingCode, mode: manifest.mode }));
}

const bridge = createBridge((command) => {
  switch (command.type) {
    case 'setAccessToken':
      token = command.payload.token;
      start().catch(failed);
      break;
    case 'showFloor':
      viewer.showFloor(command.payload.number);
      bridgeSend(event('floorChanged', { number: command.payload.number }));
      break;
    case 'showPlace':
      select(command.payload.code);
      break;
    case 'setView':
      viewer.setView(command.payload.mode);
      break;
    case 'setLabels':
      labelCodes = command.payload.codes;
      relabel();
      break;
    case 'clearSelection': {
      const previous = selected;
      selected = null;
      if (previous) repaint([previous]);
      break;
    }
    case 'highlight':
      repaint(command.payload.codes);
      break;
    case 'setAvailability': {
      const previous = [...availability.keys()];
      availability = new Map([
        ...command.payload.free.map((code) => [code, 'free'] as const),
        ...command.payload.booked.map((code) => [code, 'booked'] as const),
      ]);
      repaint(new Set([...previous, ...availability.keys()]));
      relabel();
      break;
    }
    case 'setFilter':
      break;
  }
});
bridgeSend = bridge.send;

function failed(error: unknown): void {
  bridgeSend(
    event('error', { code: 'LOAD_FAILED', message: error instanceof Error ? error.message : 'unknown' }),
  );
  if (!inApp) panelRoot.textContent = 'This building could not be loaded.';
}

start().catch(failed);
